// cesium-assets.ts

import { IonResource, Cesium3DTileset, Cesium3DTileStyle, Color, HeadingPitchRange } from 'cesium';
import type { StyleCondition } from './cesium-types';
// Note: getViewer is imported dynamically to avoid circular imports

// Load a single asset/tileset
export async function loadSingleAsset(viewer: any, id: number, isMobileDevice: boolean): Promise<Cesium3DTileset | null> {
  if (!viewer) return null;
  
  const startTime = performance.now();
  console.log(`📦 Loading tileset ${id}...`);
  
  try {
    const tileset = await Cesium3DTileset.fromUrl(
      await IonResource.fromAssetId(id)
    );
    tileset._assetId = id;
    
    // Apply performance optimizations to tileset
    if (isMobileDevice) {
      tileset.maximumScreenSpaceError = 16; // Lower quality for mobile
    } else {
      tileset.maximumScreenSpaceError = 2; // Higher quality for desktop
    }
    
    viewer.scene.primitives.add(tileset);
    
    if (!isMobileDevice) {
      viewer.scene.globe.depthTestAgainstTerrain = false;
      viewer.scene.globe.enableLighting = true;
      viewer.scene.globe.baseColor = Color.TRANSPARENT;
    }

    // Setup style change listener for this tileset
    setupTilesetStyleListener(tileset);
    
    const loadTime = performance.now() - startTime;
    console.log(`✅ Tileset ${id} loaded in ${loadTime.toFixed(2)}ms`);
    
    // Log tileset statistics when ready
    setTimeout(() => {
      console.log(`📊 Tileset ${id} stats:`, {
        boundingSphere: tileset.boundingSphere,
        maximumScreenSpaceError: tileset.maximumScreenSpaceError
      });
    }, 1000);
    
    return tileset;
  } catch (error: any) {
    console.error(`❌ Failed to load tileset ${id}:`, error);
    console.error(`Error details:`, error?.message || 'Unknown error');
    return null;
  }
}

// Setup style change event listener for a tileset
function setupTilesetStyleListener(tileset: Cesium3DTileset) {
  const updateTilesetStyle = (conditions: StyleCondition[]) => {
    try {
      const styleConditions = conditions.map(condition => [
        `\${${condition.property}} === '${condition.value}'`,
        `color('${condition.color}', ${condition.opacity})`
      ]);
      styleConditions.push(['true', "color('white', 1.0)"]);
      tileset.style = new Cesium3DTileStyle({
        color: { conditions: styleConditions }
      });
    } catch (styleError) {
      console.warn('Complex style failed, using fallback:', styleError);
      tileset.style = new Cesium3DTileStyle({ color: "color('white', 1.0)" });
    }
  };

  window.addEventListener('styleConditionsChange', ((event: CustomEvent) => {
    updateTilesetStyle(event.detail.conditions);
  }) as EventListener);
}

// Setup zoomToTileset method on viewer
export function setupZoomToTileset(viewer: any, hasInitialCameraPosition: boolean, autoZoomToAssets?: boolean) {
  viewer.zoomToTileset = (tileset: Cesium3DTileset, duration = 2.0) => {
    if (!tileset || !viewer) return;
    
    // Don't auto-zoom to assets if we have an initial camera position and autoZoomToAssets is false
    if (hasInitialCameraPosition && autoZoomToAssets === false) {
      return;
    }
    
    const boundingSphere = tileset.boundingSphere;

    const flyOptions: any = {
      duration: duration,
      offset: new HeadingPitchRange(0, -Math.PI / 4, boundingSphere.radius * 2.0)
    };

    // Set completion callback for first flight to show globe
    if (!viewer._initialFlightStarted) {
      flyOptions.complete = () => {
        if (viewer) {
          viewer.scene.globe.baseColor = Color.TRANSPARENT;
          viewer.scene.backgroundColor = Color.TRANSPARENT;
          viewer._initialFlightStarted = true;
        }
      };
    }

    viewer.camera.flyToBoundingSphere(boundingSphere, flyOptions);
  };
}

// Find tileset by asset ID
export function findAssetById(viewer: any, assetId: number): Cesium3DTileset | null {
  if (!viewer) return null;
  
  return viewer.scene.primitives._primitives.find(
    (p: any) => p instanceof Cesium3DTileset && p._assetId === assetId
  ) || null;
}

// Load and zoom to asset (internal function)
export async function loadAndZoomToAsset(viewer: any, assetId: number, duration?: number) {
  if (!viewer) {
    console.error("Cesium viewer is not initialized.");
    return;
  }
  
  // Check if asset is already loaded
  const existingAsset = findAssetById(viewer, assetId);

  if (existingAsset) {
    console.log(`Asset ${assetId} is already loaded. Zooming to it.`);
    viewer.zoomToTileset(existingAsset, duration);
    return;
  }

  // Get mobile device status from core
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const tileset = await loadSingleAsset(viewer, assetId, isMobileDevice);

  if (tileset) {
    viewer.zoomToTileset(tileset, duration);
  }
}

// Zoom to existing asset by ID (internal function)
export function zoomToAssetById(viewer: any, assetId: number, duration?: number) {
  if (!viewer) {
    console.error("Cesium viewer is not initialized.");
    return;
  }

  const tileset = findAssetById(viewer, assetId);

  if (tileset) {
    console.log(`Found asset ${assetId}, zooming...`);
    viewer.zoomToTileset(tileset, duration);
  } else {
    console.warn(`Asset with ID ${assetId} not found in the scene.`);
  }
}

// Remove asset by ID (internal function)
export function removeAssetById(viewer: any, assetId: number) {
  if (!viewer) {
    console.error("Cesium viewer is not initialized.");
    return;
  }

  const primitiveToRemove = findAssetById(viewer, assetId);

  if (primitiveToRemove) {
    viewer.scene.primitives.remove(primitiveToRemove);
    console.log(`Removed asset ${assetId} from the scene.`);
  } else {
    console.warn(`Asset with ID ${assetId} not found for removal.`);
  }
}

// Load multiple assets
export async function loadMultipleAssets(viewer: any, assetIds: number[], isMobileDevice: boolean): Promise<Cesium3DTileset[]> {
  const loadPromises = assetIds.map(id => loadSingleAsset(viewer, id, isMobileDevice));
  const results = await Promise.all(loadPromises);
  return results.filter((tileset): tileset is Cesium3DTileset => tileset !== null);
} 